import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CUSTOM_FIELD_KINDS,
  CUSTOM_FIELD_TARGETS,
  type CustomFieldKind,
  type CustomFieldTarget,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button, iconButton } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { Toggle } from '../ui/Toggle';
import { Field, field } from '../create/Field';
import {
  StaleNotice,
  UnresolvedState,
  counted,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { IconPlus, IconSettings, IconTrash } from '../icons';

const TARGET_LABEL: Record<CustomFieldTarget, string> = {
  customer: 'Customers',
  property: 'Properties',
  commercial_job: 'Jobs',
  job: 'Work orders',
  quote: 'Quotes',
  invoice: 'Invoices',
};

const KIND_LABEL: Record<CustomFieldKind, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  checkbox: 'Yes / no',
  select: 'Pick from a list',
};

type Def = {
  id: string;
  entityType: CustomFieldTarget;
  fieldKey: string;
  label: string;
  kind: CustomFieldKind;
  options: string[] | null;
  helpText: string | null;
  required: boolean;
  showOnTech: boolean;
  active: boolean;
};

/**
 * Defining custom fields (PRD v2 §2.2) — the settings side.
 *
 * Grouped by what they hang off, because "where does this question get asked" is the first
 * thing an owner is deciding. The two switches next to each field are the ones that
 * actually change behaviour: whether it's required, and whether the person at the door sees
 * it — a shop that puts all twelve of its fields on the phone buries the two that matter.
 */
export function CustomFieldsSettingsCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const q = useQuery(trpc.customField.list.queryOptions({}));
  const [adding, setAdding] = useState<CustomFieldTarget | null>(null);
  const [error, setError] = useState<string | null>(null);

  const done = () => {
    setError(null);
    void qc.invalidateQueries();
  };
  const update = useMutation(
    trpc.customField.update.mutationOptions({
      onSuccess: done,
      onError: (e) => setError(e.message),
    }),
  );
  const remove = useMutation(
    trpc.customField.remove.mutationOptions({
      onSuccess: done,
      onError: (e) => setError(e.message),
    }),
  );

  /**
   * 🔴 This card had NO pending branch and NO error branch — the only settings card with
   * neither. `(q.data ?? []) as Def[]` launders an absent answer into a typed empty array,
   * which is why the compiler never asked: a failed or still-loading query printed "Nothing
   * extra asked here" under all five headings, and left the Add button live so a shop could
   * define a field that collides with one it could not see.
   */
  const state = shownState(q, q.data);
  const defs = (q.data ?? []) as Def[];
  const byTarget = (target: CustomFieldTarget) =>
    defs.filter((d) => d.entityType === target);

  return (
    <SectionCard id={id} title={counted('Custom fields', state, defs.length)}>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <p className="mb-3 text-sm text-muted-foreground">
        The questions your shop asks that nobody else does. Answers are stored by type, so
        you can report on them later — a number stays a number.
      </p>

      {!q.data ? (
        <UnresolvedState
          state={unresolved(state)}
          what="your custom fields"
          icon={IconSettings}
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : (
        <div className="space-y-4">
          <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
          {CUSTOM_FIELD_TARGETS.map((target) => {
            const rows = byTarget(target);
            return (
              <div key={target}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {TARGET_LABEL[target]}
                  </p>
                  <Button variant="ghost" onClick={() => setAdding(target)}>
                    <IconPlus size={13} />
                    Add
                  </Button>
                </div>
                {rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nothing extra asked here.
                  </p>
                ) : (
                  <ul className="divide-y divide-border text-sm">
                    {rows.map((d) => (
                      <li
                        key={d.id}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p
                            className={`font-medium ${d.active ? '' : 'text-muted-foreground'}`}
                          >
                            {d.label}
                            {!d.active ? ' · off' : ''}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {KIND_LABEL[d.kind]}
                            {d.options ? ` · ${d.options.join(', ')}` : ''}
                            {d.helpText ? ` · ${d.helpText}` : ''}
                          </p>
                        </div>
                        <Toggle
                          label="Required"
                          on={d.required}
                          disabled={update.isPending}
                          onChange={(required) =>
                            update.mutate({ definitionId: d.id, required, ...idem() })
                          }
                        />
                        <Toggle
                          label="On the phone"
                          on={d.showOnTech}
                          disabled={update.isPending}
                          onChange={(showOnTech) =>
                            update.mutate({ definitionId: d.id, showOnTech, ...idem() })
                          }
                        />
                        <Toggle
                          label="In use"
                          on={d.active}
                          disabled={update.isPending}
                          onChange={(active) =>
                            update.mutate({ definitionId: d.id, active, ...idem() })
                          }
                        />
                        <button
                          type="button"
                          aria-label={`Delete ${d.label}`}
                          title="Delete"
                          className={iconButton}
                          disabled={remove.isPending}
                          onClick={() => remove.mutate({ definitionId: d.id, ...idem() })}
                        >
                          <IconTrash size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {adding ? <NewFieldSheet target={adding} onClose={() => setAdding(null)} /> : null}
    </SectionCard>
  );
}

function NewFieldSheet({
  target,
  onClose,
}: {
  target: CustomFieldTarget;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<CustomFieldKind>('text');
  const [optionsText, setOptionsText] = useState('');
  const [helpText, setHelpText] = useState('');
  const [required, setRequired] = useState(false);
  const [showOnTech, setShowOnTech] = useState(false);

  const create = useMutation(
    trpc.customField.create.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries();
        onClose();
      },
    }),
  );

  const options = optionsText
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  const canSave =
    label.trim().length > 0 &&
    (kind !== 'select' || options.length > 0) &&
    !create.isPending;

  return (
    <Sheet
      open
      onClose={onClose}
      title={`New field on ${TARGET_LABEL[target].toLowerCase()}`}
      footer={
        <Button
          className="w-full"
          disabled={!canSave}
          onClick={() =>
            create.mutate({
              entityType: target,
              label: label.trim(),
              kind,
              options: kind === 'select' ? options : undefined,
              helpText: helpText.trim() || undefined,
              required,
              showOnTech,
              ...idem(),
            })
          }
        >
          {create.isPending ? 'Adding…' : 'Add this field'}
        </Button>
      }
    >
      <div className="space-y-3">
        {create.error ? <Notice tone="danger">{create.error.message}</Notice> : null}

        <Field label="What are you asking">
          <input
            className={field}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Gate code"
            autoFocus
          />
        </Field>

        <Field label="What kind of answer">
          <select
            className={field}
            value={kind}
            onChange={(e) => setKind(e.target.value as CustomFieldKind)}
          >
            {CUSTOM_FIELD_KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>

        {kind === 'select' ? (
          <Field label="The choices, one per line">
            <textarea
              className="min-h-[90px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              placeholder={'Bronze\nSilver\nGold'}
            />
          </Field>
        ) : null}

        <Field label="Hint (optional)">
          <input
            className={field}
            value={helpText}
            onChange={(e) => setHelpText(e.target.value)}
            placeholder="Shown under the field"
          />
        </Field>

        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          Required
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4"
            checked={showOnTech}
            onChange={(e) => setShowOnTech(e.target.checked)}
          />
          Show this on the tech's phone
        </label>
        <p className="text-xs text-muted-foreground">
          Only put a field on the phone if the person at the door needs it. A screen with
          twelve of them buries the two that matter.
        </p>

        {/* The kind can't change later, because it would silently reinterpret every answer
            already recorded. Better to say so now than to refuse the edit later. */}
        <Notice tone="info">
          You can rename this or change its choices later, but not its type — answers are
          stored by type, and switching would reinterpret every one already recorded.
        </Notice>
      </div>
    </Sheet>
  );
}
